// ============================================================
// CombatPrompts - the small bars above the log that ask one question
// mid-attack: a start-of-turn effect, Furious Assault's bonus attack,
// an overheat, a talent re-roll, the defence chain in progress, and
// (for the attacker) whom the table is waiting on.
//
// Split from CombatLogPanel, which decides which of them are showing;
// each bar only renders its prompt and reports the answer.
// ============================================================
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/CombatLogPanel.module.css';

/** Milliseconds as "m:ss", the way every reaction clock reads. */
export function clock(ms) {
  return `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;
}

/** Start-of-turn effect: roll to shake it off (v7.7.0). The ally assist is the Half Action from rules-spec.md:537. */
export function TurnEffectPrompt({ effect, onChoice }) {
  const charId = effect.rule.endsOn?.charId?.toUpperCase();
  return (
    <div className={styles.defenceBar} role="alert">
      <span className={styles.defenceLabel}>
        {'🔥'} {effect.combatantName} is {effect.rule.label} - test {charId} to end it
        {effect.conditionMod.total !== 0 ? ` (${effect.conditionMod.labels.join(', ')})` : ''}
      </span>
      <button className={common.btnDodge} onClick={() => onChoice('roll')}>
        Roll {charId}
      </button>
      <button className={common.btnSmall} onClick={() => onChoice('assist')}>
        Ally helps (+{effect.rule.endsOn?.assistBonus})
      </button>
      <button className={common.btnSmall} onClick={() => onChoice('skip')}>
        Skip
      </button>
    </div>
  );
}

/**
 * WHOSE PROMPT IT IS (2026-10-22, plan muster): the swinger's name when you
 * now hold someone else, or nobody - a re-roll or Furious Assault answers
 * for whoever swung (hooks/attack/context `answeringContext`), so the button
 * says so. Null while you still hold them, or for a prompt with no swinger.
 */
export function promptOwner(prompt, actorId, roster) {
  if (!prompt?.actorId || prompt.actorId === actorId) return null;
  return roster.find((c) => c.id === prompt.actorId)?.name ?? null;
}

/** Furious Assault bonus attack prompt (Phase 7). `owner`: named when it is not the one you hold. */
export function BonusAttackPrompt({ owner = null, onChoice }) {
  return (
    <div className={styles.defenceBar} role="alert">
      <span className={styles.defenceLabel}>
        Furious Assault: All-Out Attack hit! Spend Reaction for a bonus Standard Attack?
      </span>
      <button className={common.btnDodge} onClick={() => onChoice(true)}>
        {owner ? `Attack again as ${owner}` : 'Attack Again'}
      </button>
      <button className={common.btnSmall} onClick={() => onChoice(false)}>
        Decline
      </button>
    </div>
  );
}

export function OverheatPrompt({ overheat, onChoice }) {
  return (
    <div className={styles.defenceBar} role="alert" data-testid="overheat-prompt">
      <span className={styles.defenceLabel}>
        {'🔥'} {overheat.slotName} OVERHEATS! Drop it, or hold on and take its damage to the arm?
      </span>
      <button className={common.btnSmall} onClick={() => onChoice('drop')}>
        Drop weapon
      </button>
      <button className={common.btnDodge} onClick={() => onChoice('hold')}>
        Hold on
      </button>
    </div>
  );
}

/** Re-roll prompt (Blademaster / Sure Strike). `owner`: named when it is not the one you hold. */
export function RerollPrompt({ reroll, owner = null, onChoice }) {
  return (
    <div className={styles.defenceBar} role="alert">
      <span className={styles.defenceLabel}>
        {reroll.talentName}: Rolled {reroll.originalRoll} (needed {'≤'}
        {reroll.modifiedSkill}) - missed.
      </span>
      <button className={common.btnDodge} onClick={() => onChoice(true)}>
        {owner ? `Re-roll for ${owner}` : 'Re-roll'}
      </button>
      <button className={common.btnSmall} onClick={() => onChoice(false)}>
        Accept Miss
      </button>
    </div>
  );
}

const hitsWord = (n) => 'hit' + (n !== 1 ? 's' : '');

function ChainStepResult({ result }) {
  return (
    <div className={clsx(styles.defenceStepResult, result.success ? styles.stepSuccess : styles.stepFailure)}>
      <span>{result.step.label}:</span>
      <span>
        {result.roll} vs {result.step.skill}
        {result.success
          ? ' ✔ avoided ' + result.avoided + ' ' + hitsWord(result.avoided)
          : ' ✘ failed! (' + result.dof + ' DoF)'}
      </span>
    </div>
  );
}

function ChainStepButton({ step, onStep }) {
  const isDodge = step.type === 'dodge' || step.type === 'step_aside_dodge';
  return (
    <button className={isDodge ? common.btnDodge : common.btnParry} onClick={() => onStep(step)}>
      {step.label} ({isDodge ? 'Ag' : 'WS'} {step.skill})
    </button>
  );
}

/** Defence chain in progress - the steps taken so far and the next ones on offer. */
export function DefenceChainBar({ chain, onStep, onAcceptRemaining }) {
  const remaining = chain.remainingHits.length;
  return (
    <div className={styles.defenceChainBar} role="alert">
      <div className={styles.defenceChainHeader}>
        <span className={styles.defenceLabel}>
          Defence Chain ({remaining} {hitsWord(remaining)} remaining)
        </span>
      </div>
      {chain.completedSteps.map((result, i) => (
        <ChainStepResult key={i} result={result} />
      ))}
      {chain.counterAttackTriggered && <div className={styles.counterAttackNote}>{'⚔'} Counter-Attack available!</div>}
      <div className={styles.defenceChainActions}>
        {chain.availableSteps.map((step) => (
          <ChainStepButton key={step.type} step={step} onStep={onStep} />
        ))}
        <button className={common.btnSmall} onClick={onAcceptRemaining}>
          Accept Remaining Hits
        </button>
        <span className={styles.defenceChainRemaining}>
          {remaining} {hitsWord(remaining)}
        </span>
      </div>
    </div>
  );
}

/** " - m:ss" for the soonest of the waits to expire (the one that decides when this clears), or null. */
function soonestClock(awaiting) {
  const left = awaiting.map((r) => r.remainingMs).filter((ms) => typeof ms === 'number');
  if (!left.length) return null;
  return ` - ${clock(Math.min(...left))}`;
}

/**
 * THE ATTACKER'S END. You fired, and now nothing is happening: this says
 * whether the table is thinking or your write went missing. It is
 * read-only on purpose - the attacker resolving their own victim's
 * reaction is exactly what the timeout is for.
 */
export function AwaitingBar({ awaiting }) {
  return (
    <div className={styles.awaitingBar} data-testid="awaiting-reactions">
      <span>
        Waiting on {awaiting.map((r) => r.name).join(', ')}
        {soonestClock(awaiting)}
      </span>
    </div>
  );
}
